import React, { useMemo } from "react";
import { map } from "lodash";
import styled from "styled-components";
import Cloud from "@charty/cloud";
import Word from "@charty/cloud/word";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizCloud } from "@radarly/models/dataviz";
import { SearchFragmentClassification } from "@radarly/models/search";
import { useRangeFromFragment } from "../../data";
import Empty from "../../empty";
import Tooltip from "../../tooltip/metric";
import { computeSeries } from "../compute";

const Container = styled.div`
  height: 100%;
`;

export interface SharedWidgetsClassificationCloudInnerProps {
  loading: boolean;
  data: SearchFragmentClassification["data"];
  fragment: SearchFragmentClassification;
  dataviz: DatavizCloud;
  onClickItem?: (uid: string | number) => void;
  className?: string;
  disableAnimation?: boolean;
}

function getCloudItems(
  items: SearchFragmentClassification["data"]["aggs"]["contentClassification"]["values"],
  metric: Metric,
  locale: Locale
) {
  return map(
    computeSeries(items, metric, "last", locale, false, true),
    ({ id, count, label, color }) => {
      return {
        id,
        label,
        color,
        weight: count,
      };
    }
  );
}

export default function SharedWidgetsClassificationCloudInner({
  loading,
  data,
  fragment,
  dataviz,
  onClickItem,
  className,
  disableAnimation,
}: SharedWidgetsClassificationCloudInnerProps) {
  const locale = useCurrentUserLocale();
  const metric = fragment.request.aggs.contentClassification.metrics[0];
  const range = useRangeFromFragment(fragment, loading);
  const items = useMemo(() => {
    return getCloudItems(
      data ? data.aggs.contentClassification.values : [],
      metric,
      locale
    );
  }, [data, metric, locale]);
  return (
    <Empty empty={!items.length}>
      <Container className={className}>
        <Cloud
          items={items}
          color={dataviz.color}
          order="label"
          limit={30}
          renderItem={(item, animated) => (
            <Word
              key={item.id}
              label={item.label}
              isHidden={item.isHidden}
              color={item.color || dataviz.color}
              normalizedWeight={item.normalizedWeight}
              locale={locale}
              weight={item.weight}
              animated={animated}
              onClick={onClickItem ? () => onClickItem(item.id) : null}
              tooltip={(serie) => (
                <Tooltip
                  range={range}
                  count={serie.weight}
                  label={serie.label}
                  metric={metric}
                  locale={locale}
                />
              )}
            />
          )}
          disableAnimation={disableAnimation}
        />
      </Container>
    </Empty>
  );
}
